/**
 * Button — brand-guide button primitive (Deep Blue Tech v3).
 *
 * Solid blue primary, 10px radius, no decorative arrow. Mirrors the
 * brand-identity cta-block button: #3B82F6 fill, white text, ~14/28 padding,
 * weight 600. The previous sliding-arrow + lift animation has been removed.
 *
 * Props:
 *   variant: 'primary' | 'ghost' | 'brand' | 'glass'
 *     - primary / brand : solid brand-blue fill, white text
 *     - ghost           : transparent with a subtle border (for dark backgrounds)
 *     - glass           : frosted white-glass (featured tier CTA)
 *   size: 'md' | 'lg' (default 'md')
 *   as: element tag to render (default 'a')
 *
 * All other props spread to the underlying element — href, onClick, etc.
 */
const base =
  'inline-flex items-center justify-center gap-2 rounded-[10px] font-semibold whitespace-nowrap ' +
  'transition-colors duration-200 border ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright/60';

const sizes = {
  md: 'px-7 py-3.5 text-body',
  lg: 'px-8 py-4 text-base',
};

const variants = {
  primary:
    'bg-brand-blue-bright text-white border-transparent hover:bg-brand-blue',
  brand:
    'bg-brand-blue-bright text-white border-transparent hover:bg-brand-blue',
  ghost:
    'bg-transparent text-ink-50 border-white/20 hover:bg-white/[.05] hover:border-white/40',
  glass:
    'bg-gradient-to-b from-white/[.14] to-white/[.05] text-ink-50 border-white/20 ' +
    'shadow-[inset_0_1px_0_rgba(255,255,255,.3),0_10px_24px_-8px_rgba(0,0,0,.6)] ' +
    'hover:border-white/35 hover:from-white/[.18] hover:to-white/[.07]',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  as: Tag = 'a',
  className = '',
  children,
  ...rest
}) {
  return (
    <Tag
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      <span>{children}</span>
    </Tag>
  );
}
